import { Slice } from 'prosemirror-model';
import { Plugin } from 'prosemirror-state';
import { findParentNode, findParentNodeOfType } from 'prosemirror-utils';
import isURL from 'validator/lib/isURL';

import { EditorView } from '@lyearn/lygo-core';

import { linkMenuKey } from '../../../plugins/LinkMenu/utils';
import { selectionMenuKey } from '../../../plugins/SelectionMenu/getSelectionPlugins';
import {
  getElementPos,
  isInCode,
  isList,
  isTable,
  selectionType,
  setPluginMeta,
  setSelection,
} from '../../../utils';

export const LINK_ELEMENT_CLASS = 'link-element';

function plugins() {
  return [
    new Plugin({
      props: {
        handleDOMEvents: {
          click: (editorView: EditorView, event: MouseEvent) => {
            if (!editorView.editable) {
              return false;
            }
            // we can have <a> element in web-bookmark block also.
            const linkElement = event
              .composedPath()
              .find(
                (element) =>
                  (element as HTMLLinkElement).localName === 'a' &&
                  (element as HTMLLinkElement).classList.contains(LINK_ELEMENT_CLASS),
              ) as HTMLLinkElement;

            if (!linkElement) {
              return false;
            }
            const elementPos = getElementPos(linkElement, editorView);
            const href = linkElement.getAttribute('href');
            const length = linkElement.textContent?.length;

            if (href && elementPos && length) {
              setSelection(editorView, selectionType.TEXT, elementPos.pos, elementPos.pos + length);

              editorView.dispatch(
                editorView.state.tr.setMeta(selectionMenuKey, {
                  open: true,
                  editorView,
                  link: href,
                }),
              );
            }

            return false;
          },
        },
        handlePaste: (view: EditorView, _event: ClipboardEvent, slice: Slice) => {
          const { state } = view;
          const { selection } = state;
          const { empty, ranges } = selection;
          const linkMark = view.state.schema.marks.link;
          const textContent = (slice.content as any).textBetween(0, slice.content.size).trim();

          if (!textContent || !isURL(textContent) || isInCode(state)) {
            return false;
          }

          if (empty) {
            view.dispatch(
              view.state.tr.replaceSelectionWith(
                view.state.schema.text(textContent, [linkMark.create({ href: textContent })]),
                false,
              ),
            );
            const parentTable = findParentNode((node) => isTable(node, view.state.schema))(
              view.state.selection,
            );
            const parentList = findParentNode((node) => isList(node, view.state.schema))(
              view.state.selection,
            );
            const nodeViewListNode = findParentNodeOfType([
              view.state.schema.nodes.toggle_list,
              view.state.schema.nodes.check_list,
            ])(view.state.selection);

            if (!parentTable && !parentList && !nodeViewListNode) {
              setPluginMeta(view, linkMenuKey, { open: true, url: textContent });
            }
          } else {
            ranges.forEach((range) => {
              const from = range.$from.pos;
              const to = range.$to.pos;

              state.doc.nodesBetween(from, to, (node, pos) => {
                const trimmedFrom = Math.max(pos, from);
                const trimmedTo = Math.min(pos + node.nodeSize, to);
                view.dispatch(
                  view.state.tr.addMark(
                    trimmedFrom,
                    trimmedTo,
                    linkMark.create({ href: textContent }),
                  ),
                );
              });
            });
          }

          return true;
        },
      },
    }),
  ];
}

export default plugins;
